import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useAdditionalProfiles } from "./useAdditionalProfiles";
import { useTimeline } from "./useTimeline";
import { supabase } from "@/integrations/supabase/client";

interface SmartDashboardData {
  hasTimeline: boolean;
  hasTimelineSections: boolean;
  hasTimelineEntries: boolean;
  managedProfilesCount: number;
  needsTimelineSetup: boolean;
}

export const useSmartDashboard = () => {
  const { user } = useAuth();
  const { additionalProfiles } = useAdditionalProfiles();

  const { data: smartData, isLoading } = useQuery({
    queryKey: ['smart-dashboard', user?.id],
    queryFn: async (): Promise<SmartDashboardData> => {
      if (!user?.id) {
        return {
          hasTimeline: false,
          hasTimelineSections: false,
          hasTimelineEntries: false,
          managedProfilesCount: 0,
          needsTimelineSetup: true
        };
      }

      // Check if user has a timeline
      const { data: timeline } = await supabase
        .from('profile_timeline')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      const hasTimeline = !!timeline;

      // Check if user has timeline sections (indicates setup completion)
      const { data: sections, count: sectionsCount } = await supabase
        .from('profile_timeline_sections')
        .select('id', { count: 'exact' })
        .eq('profile_timeline_id', timeline?.id || '')
        .eq('is_active', true);

      const hasTimelineSections = (sectionsCount || 0) > 0;

      // Check if user has timeline entries
      const { count: entriesCount } = await supabase
        .from('profile_timeline_entries')
        .select('id', { count: 'exact' })
        .eq('user_id', user.id);

      const hasTimelineEntries = (entriesCount || 0) > 0;

      return {
        hasTimeline,
        hasTimelineSections,
        hasTimelineEntries,
        managedProfilesCount: additionalProfiles?.length || 0,
        needsTimelineSetup: hasTimeline && !hasTimelineSections
      };
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  return {
    smartData: smartData || {
      hasTimeline: false,
      hasTimelineSections: false,
      hasTimelineEntries: false,
      managedProfilesCount: 0,
      needsTimelineSetup: true
    },
    isLoading
  };
};